/**
 * Dependency visualization layer
 * SVG overlay that renders dependency lines and connection bubbles
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { ITEM_HEIGHT, ITEM_LANE_HEIGHT, ITEM_LANE_HEIGHT_COMPACT } from '../TimelineItem';
import { ConnectionBubble } from './ConnectionBubble';
import { useDependencyContext } from './DependencyContext';
import { DependencyLine } from './DependencyLine';
import { DEPENDENCY_CONSTANTS, DependencyType } from './types';
import {
  distance,
  getBubblePositions,
  mouseEventToSVGCoordinates,
} from './utils/coordinateMapping';
import {
  generateDragPath,
  generateOrthogonalPath,
  getArrowheadMarkerDef,
} from './utils/pathRouting';

const { BUBBLE_RADIUS } = DEPENDENCY_CONSTANTS;

export function DependencyLayer() {
  const {
    itemPositions,
    dragState,
    hoveredItemId,
    dependencies,
    isResizing,
    startDependencyDrag,
    updateDragPosition,
    setDragTarget,
    endDependencyDrag,
    isValidDropTarget,
    setSvgRef,
    setHoveredItem,
    timelineContainerRef,
    includeDependencies,
    isCompact,
  } = useDependencyContext();

  const svgRef = useRef<SVGSVGElement>(null);
  const [svgDimensions, setSvgDimensions] = useState({ width: 0, height: 0 });

  // Register SVG ref with context
  useEffect(() => {
    if (svgRef.current) {
      setSvgRef(svgRef.current);
    }
  }, [setSvgRef]);

  // Update SVG dimensions to match the timeline content dimensions
  useEffect(() => {
    if (!timelineContainerRef?.current) return;

    const updateDimensions = () => {
      if (timelineContainerRef?.current) {
        const container = timelineContainerRef.current;
        const width = container.clientWidth;
        const height = container.clientHeight;

        setSvgDimensions({ width, height });
      }
    };

    updateDimensions();

    const resizeObserver = new ResizeObserver(updateDimensions);
    resizeObserver.observe(timelineContainerRef.current);

    return () => {
      resizeObserver.disconnect();
    };
  }, [timelineContainerRef]);

  const dependencyLines = useMemo(() => {
    const lines: Array<{
      id: string;
      sourceItemId: number;
      targetItemId: number;
      path: string;
    }> = [];
    for (const [sourceItemId, deps] of dependencies.entries()) {
      for (const dep of deps) {
        if (dep.dependencyType === DependencyType.Successor) {
          const path = generateOrthogonalPath(sourceItemId, dep.dependencyItemId, itemPositions);
          if (path) {
            lines.push({
              id: `${sourceItemId}-${dep.dependencyItemId}`,
              sourceItemId,
              targetItemId: dep.dependencyItemId,
              path,
            });
          }
        }
      }
    }

    return lines;
  }, [dependencies, itemPositions]);

  const hoveredItemBubbles = useMemo(() => {
    if (!includeDependencies) {
      return null;
    }
    if (!hoveredItemId || isResizing) {
      return null;
    }
    if (dragState.isDragging && dragState.source?.itemId !== hoveredItemId) {
      return null;
    }
    const bounds = itemPositions.get(hoveredItemId);
    if (!bounds) {
      return null;
    }

    const laneHeight = isCompact ? ITEM_LANE_HEIGHT_COMPACT : ITEM_LANE_HEIGHT;
    const bubbles = getBubblePositions(bounds, laneHeight, ITEM_HEIGHT);

    if (dragState.isDragging && dragState.source?.itemId === hoveredItemId) {
      return {
        itemId: hoveredItemId,
        left: dragState.source.type === 'left' ? dragState.source.position : bubbles.left,
        right: dragState.source.type === 'right' ? dragState.source.position : bubbles.right,
      };
    }

    return {
      itemId: hoveredItemId,
      left: bubbles.left,
      right: bubbles.right,
    };
  }, [
    includeDependencies,
    hoveredItemId,
    itemPositions,
    dragState.isDragging,
    dragState.source,
    isResizing,
    isCompact,
  ]);

  // Get bubble positions for drag target
  const dragTargetBubbles = useMemo(() => {
    if (!includeDependencies) {
      return null;
    }
    if (!dragState.isDragging || !dragState.position) return null;

    const cursorPos = dragState.position;
    for (const [itemId, bounds] of itemPositions.entries()) {
      if (itemId === dragState.source?.itemId) {
        continue;
      }
      const isOverItem =
        cursorPos.x >= bounds.x &&
        cursorPos.x <= bounds.x + bounds.width &&
        cursorPos.y >= bounds.y &&
        cursorPos.y <= bounds.y + bounds.height;

      if (isOverItem) {
        const laneHeight = isCompact ? ITEM_LANE_HEIGHT_COMPACT : ITEM_LANE_HEIGHT;
        const bubbles = getBubblePositions(bounds, laneHeight, ITEM_HEIGHT);
        return {
          itemId,
          left: bubbles.left,
          right: bubbles.right,
        };
      }
    }

    return null;
  }, [includeDependencies, dragState, itemPositions, isCompact]);

  useEffect(() => {
    if (!includeDependencies) {
      return;
    }
    if (!dragState.isDragging || !dragState.position || !dragTargetBubbles) {
      if (dragState.target) {
        setDragTarget(null);
      }
      return;
    }

    const cursorPos = dragState.position;
    const leftDist = distance(cursorPos, dragTargetBubbles.left);
    const rightDist = distance(cursorPos, dragTargetBubbles.right);
    const detectionRadius = BUBBLE_RADIUS + 10;

    let newTarget = null;

    if (dragState.source?.type === 'right' && leftDist <= detectionRadius) {
      newTarget = { itemId: dragTargetBubbles.itemId, type: 'left' as const };
    } else if (dragState.source?.type === 'left' && rightDist <= detectionRadius) {
      newTarget = { itemId: dragTargetBubbles.itemId, type: 'right' as const };
    } else if (dragState.source?.type === 'right') {
      newTarget = { itemId: dragTargetBubbles.itemId, type: 'left' as const };
    } else if (dragState.source?.type === 'left') {
      newTarget = { itemId: dragTargetBubbles.itemId, type: 'right' as const };
    }

    const currentTarget = dragState.target;
    if (newTarget?.itemId !== currentTarget?.itemId || newTarget?.type !== currentTarget?.type) {
      setDragTarget(newTarget);
    }
  }, [includeDependencies, dragState, dragTargetBubbles, setDragTarget]);

  useEffect(() => {
    if (!dragState.isDragging) return;

    const handleMouseMove = (e: MouseEvent) => {
      if (svgRef.current) {
        const svgPos = mouseEventToSVGCoordinates(e, svgRef.current);
        updateDragPosition(svgPos);
      }
    };

    const handleMouseUp = () => {
      endDependencyDrag();
    };

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        endDependencyDrag();
      }
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    window.addEventListener('keydown', handleEscape);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
      window.removeEventListener('keydown', handleEscape);
    };
  }, [dragState.isDragging, updateDragPosition, endDependencyDrag]);

  const dragLine = useMemo(() => {
    if (!dragState.isDragging || !dragState.source || !dragState.position) {
      return null;
    }
    const startPoint = dragState.source.position;
    return generateDragPath(startPoint, dragState.position);
  }, [dragState]);

  const handleBubbleDragStart = (
    itemId: number,
    type: 'left' | 'right',
    position: { x: number; y: number },
  ) => {
    if (!includeDependencies) {
      return;
    }
    startDependencyDrag(itemId, type, position);
  };

  if (!includeDependencies) {
    return null;
  }

  return (
    <svg
      ref={svgRef}
      className="absolute top-0 left-0 pointer-events-none"
      style={{
        zIndex: 500,
        width: svgDimensions.width,
        height: svgDimensions.height,
        overflow: 'visible',
      }}
    >
      <title>Dependency layer</title>
      {/* Arrowhead marker definitions */}
      {/* biome-ignore lint/security/noDangerouslySetInnerHtml: content is sanitized before rendering */}
      <defs dangerouslySetInnerHTML={{ __html: getArrowheadMarkerDef() }} />
      <g className="dependency-lines">
        {dependencyLines.map((line) => (
          <DependencyLine
            key={line.id}
            path={line.path}
            sourceItemId={line.sourceItemId}
            targetItemId={line.targetItemId}
          />
        ))}
      </g>
      {dragLine && (
        <path
          d={dragLine}
          stroke={
            dragState.target?.itemId && dragTargetBubbles
              ? '#22c55e' // green-500 when over valid target
              : '#9ca3af' // gray-400 default
          }
          strokeWidth={2}
          strokeDasharray="5,5"
          fill="none"
          style={{ pointerEvents: 'none' }}
        />
      )}
      {hoveredItemBubbles && (
        // biome-ignore lint/a11y/noStaticElementInteractions: Bubble group needs hover to maintain visibility when moving from item to bubble
        <g
          className="connection-bubbles"
          style={{ pointerEvents: 'all' }}
          onMouseEnter={() => setHoveredItem(hoveredItemBubbles.itemId)}
        >
          <ConnectionBubble
            itemId={hoveredItemBubbles.itemId}
            type="left"
            position={hoveredItemBubbles.left}
            onDragStart={handleBubbleDragStart}
            isActive={
              dragState.isDragging &&
              dragState.source?.type === 'left' &&
              dragState.source?.itemId === hoveredItemBubbles.itemId
            }
          />
          <ConnectionBubble
            itemId={hoveredItemBubbles.itemId}
            type="right"
            position={hoveredItemBubbles.right}
            onDragStart={handleBubbleDragStart}
            isActive={
              dragState.isDragging &&
              dragState.source?.type === 'right' &&
              dragState.source?.itemId === hoveredItemBubbles.itemId
            }
          />
        </g>
      )}
      {/* Connection bubbles for drag target */}
      {dragTargetBubbles && dragState.isDragging && (
        <g className="drag-target-bubbles" style={{ pointerEvents: 'all' }}>
          {dragState.source?.type === 'right' && (
            <ConnectionBubble
              itemId={dragTargetBubbles.itemId}
              type="left"
              position={dragTargetBubbles.left}
              onDragStart={handleBubbleDragStart}
              isDropTarget={true}
              isValidTarget={isValidDropTarget(dragTargetBubbles.itemId, 'left')}
            />
          )}
          {dragState.source?.type === 'left' && (
            <ConnectionBubble
              itemId={dragTargetBubbles.itemId}
              type="right"
              position={dragTargetBubbles.right}
              onDragStart={handleBubbleDragStart}
              isDropTarget={true}
              isValidTarget={isValidDropTarget(dragTargetBubbles.itemId, 'right')}
            />
          )}
        </g>
      )}
    </svg>
  );
}
